// 「現住地」（今住んでいる町）の保存と、詳細ページの各指標に添える比較行の組み立て。
// 面接で効いたのは「江東区 52% と所沢 0.02%」という並べ方だったので、
// 数値そのものではなく「今いる場所との差」を 1 行で見せる。
//
// localStorage は境界なので、壊れた値・例外の防御はこのファイルだけで行い、
// 呼び出し側は HomeSnapshot | null を無条件に信頼する（boundary-discipline）。
//
// 指標値は設定した時点のものを丸ごと持つ。こうすると比較のためにデータを取りに行かずに済み、
// SSR ページ（/m/{code}）でも SPA でも同じ 1 つの経路で比較できる。builtAt はその値が
// どの世代のデータかを表す印で、将来スナップショットの古さを判定するために置いておく。
import type { Muni } from "../../schema/muni";
import { t, type Lang } from "./i18n";
import { getMetrics, METRIC_BETTER, METRIC_GET, METRIC_ORDER, type MetricKey } from "./metrics";

export const HOME_KEY = "anju:home";

// localStorage の必要な部分だけを構造的に書く。ssr.ts 経由で Worker 側の
// tsconfig（DOM lib なし）からも読まれるので、DOM の Storage 型には依存しない。
export interface StorageReader {
  getItem(key: string): string | null;
}
export interface StorageWriter {
  setItem(key: string, value: string): void;
}
export interface StorageRemover {
  removeItem(key: string): void;
}

export interface HomeSnapshot {
  code: string;
  name: string;
  pref: string;
  /** 値を取った data/manifest.json の builtAt。 */
  builtAt: string;
  values: Record<MetricKey, number | null>;
}

/** 表示中の町の 1 指標。SSR の data-value からも Muni からも同じ形で渡せる。 */
export interface HomeSlot {
  metric: MetricKey;
  value: number | null;
}

/** 現住地と比べてどちらへ寄っているか。色の出し分けはこの 1 語だけを見る。 */
export type HomeDelta = "better" | "worse" | "same";

export interface HomeLine {
  metric: MetricKey;
  text: string;
  delta: HomeDelta;
}

export function snapshotOf(muni: Muni, builtAt: string): HomeSnapshot {
  return {
    code: muni.code,
    name: muni.name,
    pref: muni.pref,
    builtAt,
    values: metricValues((key) => METRIC_GET[key](muni)),
  };
}

/** 12 指標ぶんの値を 1 つの表に揃える。欠けた指標は null で埋める。 */
export function metricValues(pick: (key: MetricKey) => unknown): Record<MetricKey, number | null> {
  return Object.fromEntries(
    METRIC_ORDER.map((key) => {
      const v = pick(key);
      return [key, typeof v === "number" && Number.isFinite(v) ? v : null];
    }),
  ) as Record<MetricKey, number | null>;
}

export function loadHome(storage: StorageReader): HomeSnapshot | null {
  try {
    const raw = storage.getItem(HOME_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return null;
    const o = parsed as Record<string, unknown>;
    if (typeof o.code !== "string" || typeof o.name !== "string") return null;
    if (typeof o.values !== "object" || o.values === null || Array.isArray(o.values)) return null;
    const values = o.values as Record<string, unknown>;
    return {
      code: o.code,
      name: o.name,
      pref: typeof o.pref === "string" ? o.pref : "",
      builtAt: typeof o.builtAt === "string" ? o.builtAt : "",
      values: metricValues((key) => values[key]),
    };
  } catch {
    return null;
  }
}

/** プライベートモードや容量超過で setItem が投げても、設定操作自体は成立させたい。 */
export function saveHome(storage: StorageWriter, snapshot: HomeSnapshot): void {
  try {
    storage.setItem(HOME_KEY, JSON.stringify(snapshot));
  } catch {
    // 保存できないだけで、このセッションの比較は成立する
  }
}

export function clearHome(storage: StorageRemover): void {
  try {
    storage.removeItem(HOME_KEY);
  } catch {
    // 消せなくても表示は解除される
  }
}

/**
 * 表示中の町の各指標に添える比較行。現住地に値が無い指標は行そのものを出さない
 * （「—」を並べても比較にならない）。現住地そのものを開いている時も出さない。
 */
export function homeLines(
  home: HomeSnapshot,
  slots: HomeSlot[],
  lang: Lang,
  currentCode?: string,
): HomeLine[] {
  if (currentCode !== undefined && currentCode === home.code) return [];
  const metrics = getMetrics(lang);
  const lines: HomeLine[] = [];
  for (const slot of slots) {
    const homeValue = home.values[slot.metric];
    if (homeValue === null || homeValue === undefined) continue;
    lines.push({
      metric: slot.metric,
      text: t(lang).homeCompare(home.name, metrics[slot.metric].format(homeValue)),
      delta: deltaOf(slot.metric, slot.value, homeValue),
    });
  }
  return lines;
}

function deltaOf(metric: MetricKey, value: number | null, homeValue: number): HomeDelta {
  if (value === null || value === homeValue) return "same";
  const lower = value < homeValue;
  return (METRIC_BETTER[metric] === "low") === lower ? "better" : "worse";
}
